/**
 * Biomech data contracts (PoseTrack v1 / BiomechTrack v1 / ValidationSummary)
 * mirrored from docs/superpowers/specs/2026-09-21-opensim-physics-overlay-design.md.
 *
 * Kept free of three.js so the node tests can strip types and run these modules directly.
 */

export type Vec3 = [number, number, number];
export type Vec2 = [number, number];

/** MediaPipe landmark subset used by the estimator. */
export const CANONICAL_POINTS = [
  "nose",
  "left_shoulder",
  "right_shoulder",
  "left_elbow",
  "right_elbow",
  "left_wrist",
  "right_wrist",
  "left_index",
  "right_index",
  "left_hip",
  "right_hip",
  "left_knee",
  "right_knee",
  "left_ankle",
  "right_ankle",
  "left_heel",
  "right_heel",
  "left_foot_index",
  "right_foot_index",
] as const;

export type CanonicalPoint = (typeof CANONICAL_POINTS)[number];

export type PointMap = Partial<Record<CanonicalPoint, Vec3>>;
export type UvMap = Partial<Record<CanonicalPoint, Vec2>>;

export type JointId =
  | "neck"
  | "shoulder_l"
  | "shoulder_r"
  | "elbow_l"
  | "elbow_r"
  | "wrist_l"
  | "wrist_r"
  | "hip_l"
  | "hip_r"
  | "knee_l"
  | "knee_r"
  | "ankle_l"
  | "ankle_r";

export type SegmentId =
  | "head"
  | "trunk"
  | "upper_arm_l"
  | "upper_arm_r"
  | "forearm_l"
  | "forearm_r"
  | "hand_l"
  | "hand_r"
  | "thigh_l"
  | "thigh_r"
  | "shank_l"
  | "shank_r"
  | "foot_l"
  | "foot_r";

export type PersonRole = "base" | "flyer" | "spotter" | "unknown" | string;

/* ---------------------------------------------------------------- PoseTrack */

export interface PoseTrackPerson {
  id: number;
  role?: PersonRole;
  heightM?: number | null;
  massKg?: number | null;
}

/** [x, y, z, visibility?, u?, v?] */
export type PoseLandmark = number[];

export interface PoseTrackFramePerson {
  id: number;
  root?: Vec3 | null;
  landmarks: Record<string, PoseLandmark>;
}

export interface PoseTrackFrame {
  t: number;
  persons: PoseTrackFramePerson[];
}

export interface PoseTrack {
  format: "acro.pose.track";
  version: 1;
  source: "video" | "scene";
  space: "y-up-m" | "mediapipe-world";
  fps: number;
  imageAspect?: number;
  persons: PoseTrackPerson[];
  frames: PoseTrackFrame[];
}

/* ------------------------------------------------------------ BiomechTrack */

export interface BiomechJoint {
  p: Vec3;
  uv?: Vec2 | null;
  f: Vec3;
  m: Vec3;
  load: number;
}

export interface BiomechFramePerson {
  id: number;
  com: Vec3;
  joints: Partial<Record<JointId, BiomechJoint>>;
  residual: { f: Vec3; m: Vec3 };
  /** Image position of the COM (video overlays; absent/null when it cannot be placed). */
  comUv?: Vec2 | null;
}

export interface BiomechContact {
  personId: number;
  otherPersonId: number | null;
  joint: JointId | string;
  kind: "ground" | "person";
  p: Vec3;
  uv?: Vec2 | null;
  f: Vec3;
  /** Realtime only: ground contact hit the friction cone (μ = 0.8). */
  frictionClamped?: boolean;
  /** Realtime only: person contact capped at 3 body weights (excess left in the residual). */
  forceCapped?: boolean;
}

export interface BiomechFrame {
  t: number;
  persons: BiomechFramePerson[];
  contacts: BiomechContact[];
}

export interface BiomechTrackPerson {
  id: number;
  role?: PersonRole;
  massKg: number;
  heightM?: number | null;
  massSource: "input" | "proportions" | "default";
}

export interface BiomechSegmentDef {
  id: SegmentId | string;
  proximal: JointId | string;
  distal: JointId | string;
}

export interface BiomechTrack {
  format: "acro.biomech.track";
  version: 1;
  method: "opensim" | "estimate" | "mujoco";
  engine: string;
  fps: number;
  frameCount: number;
  startTime: number;
  units: { length: "m"; force: "N"; moment: "N*m" };
  up: "+y";
  gravity: Vec3;
  floorY: number;
  persons: BiomechTrackPerson[];
  joints: (JointId | string)[];
  segments: BiomechSegmentDef[];
  frames: BiomechFrame[];
  validation?: ValidationSummary | null;
}

/* ------------------------------------------------------ ValidationSummary */

export type FindingCode =
  | "residual-high"
  | "joint-overload"
  | "airborne-inconsistent"
  | "friction-violation"
  | "floor-penetration"
  | "low-confidence-pose"
  | "opensim-unavailable"
  | "opensim-failed"
  | "residual-moment-high"
  | "contact-force-capped"
  | "insufficient-data";

export interface ValidationFinding {
  code: FindingCode;
  severity: "info" | "warn" | "error";
  message: string;
  personId: number | null;
  joint: string | null;
  startTime: number;
  endTime: number;
  value: number | null;
}

export interface ValidationMetrics {
  frameCount: number;
  maxResidualForceBw: number;
  rmsResidualForceBw: number;
  maxResidualMomentBwH: number;
  maxLoad: number;
  overloadFrames: number;
  airborneInconsistentFrames: number;
  frictionViolationFrames: number;
  floorPenetrationFrames: number;
  /** Additive (calibrated scoring inputs). */
  scoredFrames?: number;
  sustainedResidualForceBw?: number;
  sustainedResidualMomentBwH?: number;
  contactForceCappedFrames?: number;
}

export interface ValidationSummary {
  status: "pass" | "warn" | "fail";
  score: number;
  method: "opensim" | "estimate" | "mujoco";
  metrics: ValidationMetrics;
  findings: ValidationFinding[];
}

/* --------------------------------------------------------- Estimator input */

export interface EstimatorPersonInput {
  id: number;
  role?: PersonRole;
  massKg?: number | null;
  heightM?: number | null;
  /** Canonical points in global Y-up metres. */
  points: PointMap;
  /** Optional normalised image coordinates (0..1, v down) for 2D projection. */
  uv?: UvMap;
}

export interface EstimatorOptions {
  /** Floor plane height in metres (default 0). */
  floorY?: number;
  /** Gravity vector, default [0, -9.81, 0]. */
  gravity?: Vec3;
  /** One-euro minimum cutoff (Hz), default 2.5. */
  minCutoff?: number;
  /** One-euro speed coefficient, default 0.7. */
  beta?: number;
  /** One-euro derivative cutoff (Hz), default 1.0. */
  dCutoff?: number;
  /** Disable filtering (used by deterministic physics tests). */
  filter?: boolean;
  /** Auto-track the floor from a rolling low percentile of slow foot/hand points (default true). */
  autoFloor?: boolean;
}

/** Localized strings for canvas legends / labels (supplied by the Blazor host). */
export interface OverlayText {
  loadLow?: string;
  loadMedium?: string;
  overload?: string;
  jointForce?: string;
  ground?: string;
  partner?: string;
  residual?: string;
  methodOpenSim?: string;
  methodEstimate?: string;
  /** Shown when the live solver (or a baked track) is MuJoCo. Defaults to "MuJoCo". */
  methodMujoco?: string;
  /** Joint id → display name. */
  joints?: Partial<Record<string, string>>;
}

export const DEFAULT_OVERLAY_TEXT: Required<Omit<OverlayText, "joints">> & { joints: Partial<Record<string, string>> } = {
  loadLow: "load < 60%",
  loadMedium: "load 60-100%",
  overload: "overload",
  jointForce: "joint force",
  ground: "ground",
  partner: "partner",
  residual: "residual",
  methodOpenSim: "OpenSim",
  methodEstimate: "Estimate",
  methodMujoco: "MuJoCo",
  joints: {},
};

/** Overlay draw/render option flags shared by the 2D and 3D renderers. */
export interface OverlayOptions {
  forces: boolean;
  torques: boolean;
  contacts: boolean;
  labels: boolean;
  residual: boolean;
  /** Localized legend/label strings; English defaults when absent. */
  text?: OverlayText;
}

/** Merge host-supplied text over the defaults (joint names merged key by key). */
export function resolveOverlayText(text?: OverlayText | null): typeof DEFAULT_OVERLAY_TEXT {
  const out = { ...DEFAULT_OVERLAY_TEXT, joints: { ...DEFAULT_OVERLAY_TEXT.joints } };
  if (!text || typeof text !== "object") return out;
  for (const key of Object.keys(DEFAULT_OVERLAY_TEXT) as (keyof typeof DEFAULT_OVERLAY_TEXT)[]) {
    if (key === "joints") continue;
    const value = (text as Record<string, unknown>)[key];
    if (typeof value === "string" && value.length > 0) (out as Record<string, unknown>)[key] = value;
  }
  if (text.joints && typeof text.joints === "object") {
    for (const [id, name] of Object.entries(text.joints)) {
      if (typeof name === "string" && name.length > 0) out.joints[id] = name;
    }
  }
  return out;
}

/** Display name of a joint id (localized when the host supplied one). */
export function jointDisplayName(text: typeof DEFAULT_OVERLAY_TEXT, jointId: string): string {
  return text.joints[jointId] ?? jointId.replace(/_/g, " ");
}

export const DEFAULT_OVERLAY_OPTIONS: OverlayOptions = {
  forces: true,
  torques: true,
  contacts: true,
  labels: false,
  residual: false,
};
